import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Search, Plus, Eye, Edit, Trash2, Calendar, Star, Clock, AlertCircle, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";
import { useDecisions, Decision } from "@/hooks/useDecisions";
import { useFormatDate } from "../src/hooks/useFormatDate";
import { useMobile } from "../src/hooks/use-mobile";
import { classifyDecisionReviews } from "@/lib/review-classification";
import { clickableCardProps } from "@/lib/clickable-card";

interface MyDecisionsProps {
  onViewDecision: (id: string) => void;
  onEditDecision: (id: string) => void;
  onNewDecision: () => void;
  initialTagFilter?: string;
}

export function MyDecisions({ 
  onViewDecision, 
  onEditDecision, 
  onNewDecision,
  initialTagFilter
}: MyDecisionsProps) {
  const { decisions, deleteDecision, getCurrentRating, getPendingReviews } = useDecisions();
  const { formatDate } = useFormatDate();
  const isMobile = useMobile();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState<string>(initialTagFilter || "all");
  const [sortBy, setSortBy] = useState<string>("date-desc");

  const allTags = Array.from(new Set(decisions.flatMap(d => d.tags.filter(tag => tag && tag.name).map(tag => tag.name))));

  const filteredDecisions = decisions
    .filter(decision => {
      const matchesSearch = decision.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (decision.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           decision.tags.filter(tag => tag && tag.name).some(tag => tag.name.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesTag = selectedTag === "all" || decision.tags.filter(tag => tag && tag.name).some(tag => tag.name === selectedTag);
      return matchesSearch && matchesTag;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "date-desc":
          return new Date(b.decision_date).getTime() - new Date(a.decision_date).getTime();
        case "date-asc":
          return new Date(a.decision_date).getTime() - new Date(b.decision_date).getTime();
        case "rating-desc":
          return getCurrentRating(b) - getCurrentRating(a);
        case "rating-asc":
          return getCurrentRating(a) - getCurrentRating(b);
        case "initial-rating-desc":
          return b.day0_rating - a.day0_rating;
        case "initial-rating-asc":
          return a.day0_rating - b.day0_rating;
        case "trend-desc":
          return (getCurrentRating(b) - b.day0_rating) - (getCurrentRating(a) - a.day0_rating);
        case "trend-asc":
          return (getCurrentRating(a) - a.day0_rating) - (getCurrentRating(b) - b.day0_rating);
        case "name-asc":
          return a.title.localeCompare(b.title);
        case "name-desc":
          return b.title.localeCompare(a.title);
        default:
          return 0;
      }
    });

  const getReviewStatusColor = (status: string) => {
    switch (status) {
      case "completed": return "bg-green-100 text-green-800";
      case "overdue": return "bg-red-100 text-red-800";
      case "upcoming": return "bg-gray-100 text-gray-600";
      case "due_now": return "bg-blue-100 text-blue-800";
      default: return "bg-gray-100 text-gray-600";
    }
  };

  const getRatingTrend = (decision: Decision) => {
    const currentRating = getCurrentRating(decision);
    const initialRating = decision.day0_rating;
    const diff = currentRating - initialRating;
    
    if (diff > 0.1) return { icon: TrendingUp, color: "text-green-600", value: `+${diff.toFixed(1)}` };
    if (diff < -0.1) return { icon: TrendingDown, color: "text-red-600", value: diff.toFixed(1) };
    return { icon: Minus, color: "text-gray-500", value: "0" };
  };

  const pendingReviews = getPendingReviews();

  // const isOverdue = (dueDate?: string) => {
  //   return dueDate && new Date(dueDate) < new Date();
  // };

  return (
    <div className={`max-w-4xl mx-auto ${isMobile ? 'px-4 py-6' : 'px-6 py-8'}`}>
        <div className={`flex items-center justify-between ${isMobile ? 'mb-4' : 'mb-6'}`}>
          <div className="min-w-0 flex-1">
            <h1 className={isMobile ? "text-xl" : ""}>My Decisions</h1>
            <p className={`text-muted-foreground ${isMobile ? 'text-sm' : ''}`}>
              Manage and review all your recorded decisions
            </p>
          </div>
          <Button onClick={onNewDecision} size={isMobile ? "sm" : "default"} className="ml-4 flex-shrink-0">
            <Plus className="h-4 w-4 mr-2" />
            {isMobile ? "New" : "New Decision"}
          </Button>
        </div>

        <Tabs defaultValue="all" className="space-y-6">
          <TabsList>
            <TabsTrigger value="all">All Decisions ({decisions.length})</TabsTrigger>
            <TabsTrigger value="pending">Pending Reviews ({pendingReviews.length})</TabsTrigger>
          </TabsList>

          <div className={`flex items-center gap-4 ${isMobile ? 'flex-col space-y-2' : ''}`}>
            <div className={`relative ${isMobile ? 'w-full' : 'flex-1 max-w-md'}`}>
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search decisions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className={`flex gap-2 ${isMobile ? 'w-full' : ''}`}>
              <Select value={selectedTag} onValueChange={setSelectedTag}>
                <SelectTrigger className={isMobile ? "flex-1" : "w-40"}>
                  <SelectValue placeholder="Filter by tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {allTags.map(tag => (
                    <SelectItem key={tag} value={tag}>
                      {tag}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className={isMobile ? "flex-1" : "w-56"}>
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date-desc">Date (Newest)</SelectItem>
                  <SelectItem value="date-asc">Date (Oldest)</SelectItem>
                  <SelectItem value="rating-desc">Current Rating (Highest)</SelectItem>
                  <SelectItem value="rating-asc">Current Rating (Lowest)</SelectItem>
                  <SelectItem value="initial-rating-desc">Initial Rating (Highest)</SelectItem>
                  <SelectItem value="initial-rating-asc">Initial Rating (Lowest)</SelectItem>
                  <SelectItem value="trend-desc">Trend (Most Improved)</SelectItem>
                  <SelectItem value="trend-asc">Trend (Least Improved)</SelectItem>
                  <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                  <SelectItem value="name-desc">Name (Z-A)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <TabsContent value="all">
            <div className="space-y-4">
              {filteredDecisions.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <p className="text-muted-foreground mb-4">
                      {searchTerm || selectedTag !== "all" 
                        ? "No decisions match your filters" 
                        : "No decisions recorded yet"
                      }
                    </p>
                    <Button onClick={onNewDecision}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Your First Decision
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                filteredDecisions.map((decision) => {
                  const currentRating = getCurrentRating(decision);
                  const trend = getRatingTrend(decision);
                  const TrendIcon = trend.icon;
                  
                  return (
                    <Card
                      key={decision.id}
                      className="group cursor-pointer hover:shadow-md transition-shadow"
                      {...clickableCardProps(() => onViewDecision(decision.id))}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                             <CardTitle className="text-lg group-hover:text-primary">
                               {decision.title}
                             </CardTitle>
                            <CardDescription className="mt-1">
                              {decision.description}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-3 ml-4">
                            <div className="text-right">
                              <div className="flex items-center gap-1">
                                <Star className="h-4 w-4 text-yellow-500" />
                                <span className="text-sm font-medium">{currentRating}/10</span>
                                 {decision.rating_history.length > 1 && (
                                  <>
                                    <TrendIcon className={`h-3 w-3 ml-1 ${trend.color}`} />
                                    <span className={`text-xs ${trend.color}`}>{trend.value}</span>
                                  </>
                                )}
                              </div>
                               {decision.rating_history.length > 1 && (
                                 <div className="text-xs text-muted-foreground">
                                   Initial: {decision.day0_rating}/10
                                 </div>
                               )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                           <div className="flex items-center gap-1">
                             <Calendar className="h-4 w-4" />
                              {formatDate(decision.decision_date)}
                            </div>
                           <div className={`flex gap-1 ${isMobile ? 'flex-wrap' : ''}`}>
                              {(() => {
                                const tags = decision.tags.filter(tag => tag && tag.name);
                                const displayTags = isMobile ? tags.slice(0, 2) : tags;
                                const remainingCount = isMobile ? tags.length - 2 : 0;
                                
                                return (
                                  <>
                                    {displayTags.map((tag) => (
                                      <Badge key={tag.id} variant="secondary" className={isMobile ? "text-xs px-1.5 py-0.5" : "text-xs"}>
                                        {tag.name}
                                      </Badge>
                                    ))}
                                    {isMobile && remainingCount > 0 && (
                                      <Badge variant="outline" className="text-xs px-1.5 py-0.5 text-muted-foreground">
                                        +{remainingCount}
                                      </Badge>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                           {decision.rating_history.length > 1 && (
                             <div className="flex items-center gap-1">
                               <Clock className="h-4 w-4" />
                               <span>{decision.rating_history.length - 1} reviews completed</span>
                             </div>
                           )}
                        </div>
                      </CardHeader>
                      
                      <CardContent>
                         <div className={`${isMobile ? 'flex-col space-y-3' : 'flex items-center justify-between'}`}>
                           <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium">Reviews:</span>
                              {decision.scheduled_reviews.map((review, index) => (
                                <Badge 
                                  key={index} 
                                  variant="outline" 
                                  className={`text-xs ${getReviewStatusColor(review.status)} ${isMobile ? 'px-1.5 py-0.5' : ''}`}
                                >
                                  {isMobile ? `${review.day}d` : `${review.day}d ${review.status}`}
                                </Badge>
                              ))}
                               {(() => {
                                 const { dueNowReviews, overdueReviews } = classifyDecisionReviews(decision);
                                 return (dueNowReviews.length > 0 || overdueReviews.length > 0);
                               })() && (
                                <Badge variant="destructive" className={`text-xs ${isMobile ? 'px-1.5 py-0.5' : ''}`}>
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  {isMobile ? "Due" : "Review Due"}
                                </Badge>
                              )}
                           </div>
                           
                           <div className="flex items-center gap-2">
                             <Button 
                               variant="ghost" 
                               size="sm"
                               onClick={() => onViewDecision(decision.id)}
                               className={isMobile ? "h-8 w-8 p-0" : ""}
                             >
                               <Eye className="h-4 w-4" />
                               {!isMobile && <span className="sr-only">View</span>}
                             </Button>
                             
                             <Button 
                               variant="ghost" 
                               size="sm"
                               onClick={() => onEditDecision(decision.id)}
                               className={isMobile ? "h-8 w-8 p-0" : ""}
                             >
                               <Edit className="h-4 w-4" />
                               {!isMobile && <span className="sr-only">Edit</span>}
                             </Button>
                             
                             <AlertDialog>
                               <AlertDialogTrigger asChild>
                                 <Button variant="ghost" size="sm" className={isMobile ? "h-8 w-8 p-0" : ""}>
                                   <Trash2 className="h-4 w-4" />
                                   {!isMobile && <span className="sr-only">Delete</span>}
                                 </Button>
                               </AlertDialogTrigger>
                               <AlertDialogContent>
                                 <AlertDialogHeader>
                                   <AlertDialogTitle>Delete Decision</AlertDialogTitle>
                                   <AlertDialogDescription>
                                     Are you sure you want to delete "{decision.title}"? This action cannot be undone.
                                   </AlertDialogDescription>
                                 </AlertDialogHeader>
                                 <AlertDialogFooter>
                                   <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => deleteDecision(decision.id)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                     Delete
                                   </AlertDialogAction>
                                 </AlertDialogFooter>
                               </AlertDialogContent>
                             </AlertDialog>
                           </div>
                         </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          <TabsContent value="pending">
            <div className="space-y-4">
              {pendingReviews.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Clock className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No pending reviews</p>
                  </CardContent>
                </Card>
              ) : (
                pendingReviews.map((decision) => {
                  const currentRating = getCurrentRating(decision);
                  
                  return (
                    <Card
                      key={decision.id}
                      className="group cursor-pointer border-yellow-200 hover:shadow-md transition-shadow"
                      {...clickableCardProps(() => onViewDecision(decision.id))}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                             <CardTitle className="text-lg group-hover:text-primary">
                               {decision.title}
                             </CardTitle>
                             <CardDescription className="mt-1">
                               {decision.description}
                             </CardDescription>
                           </div>
                           <div className="flex items-center gap-2">
                             <div className="text-right">
                               <div className="flex items-center gap-1">
                                 <Star className="h-4 w-4 text-yellow-500" />
                                 <span className="text-sm font-medium">{currentRating}/10</span>
                               </div>
                               <div className="text-xs text-muted-foreground">
                                 Initial: {decision.day0_rating}/10
                               </div>
                            </div>
                            <Badge variant="destructive">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Review Due
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                      
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                             <span className="text-sm font-medium">Pending Reviews:</span>
                               {(() => {
                                 const { dueNowReviews, overdueReviews } = classifyDecisionReviews(decision);
                                 return [...dueNowReviews, ...overdueReviews];
                               })().map((review, index) => (
                                 <Badge 
                                   key={index} 
                                   variant="outline" 
                                   className="text-xs bg-yellow-100 text-yellow-800"
                                 >
                                   Day {review.day} ({review.due_date})
                                 </Badge>
                               ))}
                          </div>
                          
                          <Button 
                            onClick={() => onViewDecision(decision.id)}
                            className="bg-yellow-600 hover:bg-yellow-700"
                          >
                            Complete Review
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>
        </Tabs>
    </div>
  );
}